import React from 'react';

import type { Theme } from '@mui/material';
import { Typography, useTheme } from '@mui/material';
import { CircleX } from 'lucide-react';
import {
  ATTENTION_AMBER,
  CRMAttentionIcon,
  CRMConnectedIcon,
} from 'src/components/icons/CRMConnectedIcon';
import { makeStyles } from 'tss-react/mui';

export type StatusBadgeStatus = 'error' | 'neutral' | 'success' | 'warning';

export interface StatusBadgeProps {
  readonly label: string;
  readonly status: StatusBadgeStatus;
  readonly className?: string;
  readonly 'data-cy'?: string;
  /** For a badge whose status icon is already shown elsewhere, e.g. on the logo beside it. */
  readonly hideIcon?: boolean;
}

const STATUS_COLOR = {
  error: theme => theme.palette.error.main,
  neutral: theme => theme.palette.text.secondary,
  success: theme => theme.appColors.connectedCheck,
  warning: () => ATTENTION_AMBER,
} satisfies Record<StatusBadgeStatus, (theme: Theme) => string>;

const useStyles = makeStyles<{ status: StatusBadgeStatus }>()((theme, { status }) => ({
  badge: {
    alignItems: 'center',
    color: STATUS_COLOR[status](theme),
    display: 'inline-flex',
    fontSize: 12,
    fontWeight: 400,
    gap: theme.spacing(0.5),
  },
  icon: {
    flexShrink: 0,
    height: 16,
    width: 16,
  },
}));

function ErrorStatusIcon({ className }: { readonly className?: string }): JSX.Element {
  const theme = useTheme();
  return <CircleX className={className} color={theme.palette.error.main} />;
}

const STATUS_ICON: Record<StatusBadgeStatus, React.ComponentType<{ className?: string }> | null> = {
  error: ErrorStatusIcon,
  neutral: null,
  success: CRMConnectedIcon,
  warning: CRMAttentionIcon,
};

/** The icon a status maps to, on its own: e.g. a badge on the corner of a logo. */
export function StatusIcon({
  className,
  status,
}: {
  readonly status: StatusBadgeStatus;
  readonly className?: string;
}): JSX.Element | null {
  const Icon = STATUS_ICON[status];
  return Icon && <Icon className={className} />;
}

/** A status label in the status's color, led by its icon. */
export const StatusBadge = React.memo(function StatusBadge({
  className,
  'data-cy': dataCy,
  hideIcon = false,
  label,
  status,
}: StatusBadgeProps): JSX.Element {
  const { classes, cx } = useStyles({ status });

  return (
    <Typography className={cx(classes.badge, className)} data-cy={dataCy}>
      {!hideIcon && <StatusIcon className={classes.icon} status={status} />}
      {label}
    </Typography>
  );
});
